import { toSingleLangTagsFrom } from '@please.com/serverless-models/methods/tag';
import { ObjectID } from 'bson';
import _ from 'lodash';
import { bookingStatuses, fallbackLanguage, MINUTES_IN_A_DAY, PayAt } from '../constants';
import { ILocalizedText, LanguageKeys } from '../schemas/localized-text';
import { IService } from '../schemas/service';
import {
  IServiceGroup,
  IServiceGroupDocument,
  IServiceGroupSingleLang,
  localizedFields,
} from '../schemas/serviceGroup';
import { IServiceOrganization } from '../schemas/serviceOrganization';
import { ITopLocation } from '../schemas/top-location';
import { toSingleLangLocation } from './location';
import { isAccommodation, toSingleLangService } from './service';
import { getPriceForPeople } from './service-price';

const objectIdComparator = (a: ObjectID, b: ObjectID | string) => a.equals(b);

interface ITripWithJustServiceIds {
  services: Array<Pick<IServiceOrganization, 'service'>>;
}
export function getUniqueServiceIds(trips: ITripWithJustServiceIds[]): ObjectID[] {
  const allServiceIds = _.flatMap(trips, trip => _.map(trip.services, 'service'));
  const uniqServices = _.uniqWith(allServiceIds, objectIdComparator);
  return uniqServices;
}

export function isFastBookable(services: Array<Pick<IService, 'checkoutOptions'>>) {
  const threshold = Math.round(services.length / 2.0);
  const fastBookableServices = services.filter(service =>
    service.checkoutOptions ? service.checkoutOptions.payAt === PayAt.please : false,
  );
  return fastBookableServices.length >= threshold;
}

type IGetNumberOfDaysInput = Pick<IServiceGroup, 'duration'> & {
  services: Array<Pick<IServiceOrganization, 'day'>>;
};
export function getNumberOfDays(trip: IGetNumberOfDaysInput) {
  return Math.ceil(trip.duration / MINUTES_IN_A_DAY);
}

/**
 * This method is useful when you want to know if a trip was even booked
 * For example:
 * - if you don't want to allow to modify a trip after booking once
 * - if you don't want to allow to modify a cancelled trip
 */
export function wasTripEverBooked(trip: Pick<IServiceGroup, 'bookingStatus'>): boolean {
  if (!trip.bookingStatus) return false;
  if (trip.bookingStatus === bookingStatuses.booked) return true;
  if (trip.bookingStatus === bookingStatuses.cancelledByProvider) return true;
  if (trip.bookingStatus === bookingStatuses.cancelledByUser) return true;
  return false;
}

/**
 * Calculates a rough price from the given peopleCount
 */
export function calculateTripPriceFromPricing(
  trip: IServiceGroup,
  adults: number,
  children: number,
  infants: number,
) {
  const { pricing } = trip;
  const existingPricing = _.find(pricing, { adults, children, infants });
  if (existingPricing) return _.round(existingPricing.price, 2);

  const commonPeopleCount = adults + children;
  const twoAdultsPricing = _.find(pricing, { adults: 2, children: 0 });
  const oneAdultPricing = _.find(pricing, { adults: 1, children: 0 });
  const twoAdultsMultiplier = Math.floor(commonPeopleCount / 2);
  const oneAdultMultiplier = commonPeopleCount % 2;

  const twoPeopleCost =
    (twoAdultsPricing && twoAdultsPricing.price) ||
    (oneAdultPricing && oneAdultPricing.price) ||
    trip.totalPrice;
  const onePersonCost =
    (oneAdultPricing && oneAdultPricing.price) ||
    (twoAdultsPricing && twoAdultsPricing.price) ||
    trip.totalPrice;
  const price = twoAdultsMultiplier * twoPeopleCost + oneAdultMultiplier * onePersonCost;
  return _.round(price, 2);
}

/**
 * Calculates the price of a trip
 * @returns [totalPrice, bookablePrice]
 */
export function calculateTripPrice(
  trip: Pick<IServiceGroup, 'services' | 'adultCount' | 'childrenCount' | 'infantCount'>,
  services: Array<Pick<IService, '_id' | 'basePrice' | 'categories' | 'checkoutOptions'>>,
): [number, number] {
  const serviceById = _.keyBy(services, '_id');
  const { adultCount = 1, childrenCount = 0 } = trip;
  const peopleCount = { adultCount, childrenCount };
  const bookablePrices = trip.services.map(org =>
    getBookableServicePrice(org, serviceById[org.service.toString()], peopleCount),
  );
  const totalPrices = trip.services.map(org =>
    getNonBookableServicePrice(org, serviceById[org.service.toString()], peopleCount),
  );
  const rawBookableSum = _.sum(bookablePrices);
  const rawTotalSum = _.sum(totalPrices);
  return [_.round(rawTotalSum, 2), _.round(rawBookableSum, 2)];
}
function getBookableServicePrice(
  serviceOrg: IServiceOrganization,
  service: Pick<IService, '_id' | 'basePrice' | 'categories' | 'checkoutOptions'>,
  { adultCount, childrenCount }: { adultCount: number; childrenCount: number },
): number {
  if (service.checkoutOptions && service.checkoutOptions.payAt !== PayAt.please) return 0;
  if (serviceOrg.selectedOption) {
    return getPriceForPeople(serviceOrg.selectedOption.price, { adultCount, childrenCount });
  }
  return getPriceForPeople(service.basePrice, { adultCount, childrenCount });
}
function getNonBookableServicePrice(
  serviceOrg: IServiceOrganization,
  service: Pick<IService, '_id' | 'basePrice' | 'categories' | 'checkoutOptions'>,
  { adultCount, childrenCount }: { adultCount: number; childrenCount: number },
): number {
  if (isAccommodation(service)) {
    return getBookableServicePrice(serviceOrg, service, { adultCount, childrenCount });
  }
  return getPriceForPeople(service.basePrice, { adultCount, childrenCount });
}

/**
 * Calculates the TOP 3 locations where most of the services in the trip are
 */
export function getTopLocations(
  services: Array<Pick<IService, 'location'>>,
  topN = 3,
): ITopLocation[] {
  const locations = services.map(s => s.location);
  const sortDescByCount = (a: { count: number }, b: { count: number }) => b.count - a.count;

  interface ICountryCount {
    countryCode: string;
    count: number;
  }
  const countryCounts = locations
    .reduce(
      (accumulator, { countryCode }) => {
        const countryEntry = _.find(accumulator, { countryCode });
        if (countryEntry) countryEntry.count += 1;
        else accumulator.push({ countryCode, count: 1 });
        return accumulator;
      },
      [] as ICountryCount[],
    )
    .sort(sortDescByCount);
  if (countryCounts.length > 1) {
    return countryCounts.slice(0, topN).map(c => _.omit(c, 'count'));
  }

  interface IStateCount extends ICountryCount {
    state?: string;
  }
  const stateCounts = locations
    .reduce(
      (accumulator, { countryCode, state }) => {
        const stateEntry = _.find(accumulator, { countryCode, state });
        if (stateEntry) stateEntry.count += 1;
        else accumulator.push({ countryCode, state, count: 1 });
        return accumulator;
      },
      [] as IStateCount[],
    )
    .sort(sortDescByCount);
  if (stateCounts.length > 1) {
    return stateCounts.slice(0, topN).map(s => _.omit(s, 'count'));
  }

  interface ICityCount extends IStateCount {
    city: string;
  }
  const cityCounts = locations
    .reduce(
      (accumulator, { countryCode, state, city }) => {
        if (!city) return accumulator;
        const cityEntry = _.find(accumulator, { countryCode, state, city });
        if (cityEntry) cityEntry.count += 1;
        else accumulator.push({ countryCode, state, city, count: 1 });
        return accumulator;
      },
      [] as ICityCount[],
    )
    .sort(sortDescByCount);
  return cityCounts.slice(0, topN).map(c => _.omit(c, 'count'));
}

export function toSingleLangTrip(
  trip: Partial<IServiceGroup>,
  lang: LanguageKeys,
): IServiceGroupSingleLang {
  if (typeof (trip as IServiceGroupDocument).toObject === 'function') {
    throw new Error('service object can not be a mongoose document, it has to be a POJO');
  }
  const clonedTrip = _.cloneDeep(trip);
  for (const fieldKey of localizedFields) {
    const localizedValue = _.get(clonedTrip, fieldKey);
    if (!localizedValue) continue;
    if (_.isArray(localizedValue)) {
      for (let i = 0; i < localizedValue.length; i++) {
        const element = localizedValue[i];
        if (element && _.isObject(element)) {
          _.set(
            clonedTrip,
            `${fieldKey}[${i}]`,
            (element as ILocalizedText)[lang] || (element as ILocalizedText)[fallbackLanguage],
          );
        }
      }
    } else if (_.isObject(localizedValue)) {
      _.set(clonedTrip, fieldKey, localizedValue[lang] || localizedValue[fallbackLanguage]);
    }
  }

  if (clonedTrip.tags) toSingleLangTagsFrom(clonedTrip.tags, lang);

  clonedTrip.services = (clonedTrip.services || []).map(sOrg => {
    if (!(sOrg.service instanceof ObjectID) && typeof sOrg.service !== 'string') {
      sOrg.service = toSingleLangService(sOrg.service, lang) as any;
    }
    return sOrg;
  });

  clonedTrip.notes = _.mapValues(clonedTrip.notes || {}, note => {
    if (note && note.en) return note.en;
    if (note && note['en-us']) return note['en-us'];
    return note;
  });
  if (clonedTrip.location) toSingleLangLocation(clonedTrip.location, lang);
  return (clonedTrip as unknown) as IServiceGroupSingleLang;
}
